import React from 'react';
import { connect } from 'react-redux';
import store2 from 'store2';
import { getInspectionStandardTypeOptions } from '../../customerSettings';
import { customInspectionTypeFrontEndSettings } from '../../customInspectionTypes/customInspectionTypeFrontEndSettings';
import { IWithReactRouterProps } from '../../mixins/router/withReactRouter';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchGet } from '../../store/fetch';
import { codeToColorCode } from '../../utils/conditionHelper';
import { InspectionStandardTypes } from '../../utils/enums/InspectionStandardTypes';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { EditPanelFilterMultiSelect, InputSearchText, ISelectOption, SmallFilterSelect, Topbar, TopbarLeft, TopbarCrumbActive } from '../elements';
import { Spinner } from '../spinners';
import { Component } from '../utils/Component';
import { DisplayMessage } from '../utils/displayMessage';
import { transformLabelsToSelectOptions } from '../utils/entityLabels.utils';
import { Bounds, GenericMap, IMarker } from '../utils/GenericMap';
import { getConditionLabelForMap } from '../utils/getConditionLabelForMap';

interface ISurveysMapProps extends ITranslateProps, IWithReactRouterProps {
    languageCode: string;
    useBatches: boolean;
    isPdokMapLayerAvailable: boolean;
    isDroneNoFlyMapNlAvailable: boolean;
    shouldClusterMarkers: boolean;
    useProductTitle: boolean;
}

interface IObject {
    id: string;
    name: string;
    location: string;
    latitude: number;
    longitude: number;
    lastSurveyCondition?: string | undefined;
    lastSurveyId?: string | undefined;
    lastSurveyInspectionStandardType?: string | undefined;
    surveryedOn?: Date | undefined;
    shapeGeoJson?: string | undefined;
    objectTypeName?: string | undefined;
}

interface IValueText {
    value: string;
    text: string;
}

interface ISurveysMapState {
    loading: boolean;
    searching: boolean;
    noDataAvailable: boolean;
    items: IObject[];
    latitude: number;
    longitude: number;
    currentIndex: number;
    currentTitle: string;
    currentSurveyDate: Date;
    currentId: string;
    objectTypes: IValueText[];
    batches: IValueText[];
    objectComplexLocations: IValueText[];
    objectTypeId?: string | undefined;
    batchId?: string | undefined;
    inspectionStandardType?: string | undefined;
    conditions?: string[] | undefined;
    riskAssessment?: string | undefined;
    locationCode?: string | undefined;
    codeDescription?: string | undefined;
    knownLabels: ISelectOption[];
    selectedSurveyLabels: string[];
    mapBounds?: Bounds | undefined;
    markersToFit?: IMarker[] | undefined;
    isFirstSearch: boolean;
}

function mapStateToProps(state: any) {
    const { authentication, language } = state;
    return {
        useBatches: authentication.useBatches,
        languageCode: language.code,
        isPdokMapLayerAvailable: authentication.isPdokMapLayerAvailable,
        isDroneNoFlyMapNlAvailable: authentication.isDroneNoFlyMapNlAvailable,
        shouldClusterMarkers: authentication.shouldClusterMarkers,
        useProductTitle: authentication.useProductTitle,
    };
}

const storeKey = 'ObjectTypesList';

class SurveysMap extends Component<ISurveysMapProps, ISurveysMapState> {
    constructor(props: ISurveysMapProps) {
        super(props);

        this.state = {
            loading: true,
            searching: false,
            noDataAvailable: false,
            items: [],
            latitude: 51.808504,
            longitude: 5.703935,
            currentIndex: 0,
            currentTitle: '',
            currentSurveyDate: new Date(),
            currentId: '',
            objectTypes: [],
            batches: [],
            knownLabels: [],
            selectedSurveyLabels: [],
            objectComplexLocations: [],
            isFirstSearch: true,
        };
    }

    public override async componentDidMount() {
        if (isCustomerVersion('rijksvastgoed')) {
            await this.setState({ locationCode: store2.get(storeKey) });
        } else {
            await this.setState({ objectTypeId: store2.get(storeKey) });
        }
        await this.search();
        if (this.props.useBatches) {
            await this.loadBatches();
        }
        void this.loadObjectTypes();
        if (isCustomerVersion('rijksvastgoed')) {
            await this.loadObjectComplexLocations();
        }

        await this.loadKnownLabels();
    }

    public override async componentDidUpdate(prevProps: ISurveysMapProps, prevState: ISurveysMapState) {
        if (this.props.languageCode !== prevProps.languageCode) {
            await this.search();
        }
        if (
            this.state.batchId !== prevState.batchId ||
            this.state.riskAssessment !== prevState.riskAssessment ||
            this.state.locationCode !== prevState.locationCode
        ) {
            await this.search();
        } else if (this.state.inspectionStandardType !== prevState.inspectionStandardType) {
            if (this.props.useBatches) {
                await this.loadBatches();
            }
            await this.search();
        } else if (
            this.state.objectTypeId !== prevState.objectTypeId ||
            this.state.inspectionStandardType !== prevState.inspectionStandardType ||
            this.state.conditions !== prevState.conditions
        ) {
            await this.search();
        }
    }

    public override render() {
        return (
            <span>
                <Topbar>
                    <TopbarLeft>
                        <TopbarCrumbActive
                            title={this.props.useProductTitle ? this.props.t('common.productReports') : this.props.t('common.surveys')}
                            icon="fa-camera"
                        />
                    </TopbarLeft>
                </Topbar>

                <section id="content" className="pn posr">
                    <div style={{ position: 'relative', height: '100%' }}>
                        <form method="post" action="/" id="filter">
                            <div className="map-menu admin-form">
                                {isCustomerVersion('rijksvastgoed') && (
                                    <div className="mt10">
                                        <SmallFilterSelect
                                            name="locationCode"
                                            value={this.state.locationCode}
                                            handleChange={this.handleChange.bind(this)}
                                            topLabel={this.props.t('common.location')}
                                            placeholder={this.props.t('common.findItem', this.props.t('common.location'))}
                                            options={this.state.objectComplexLocations}
                                        />
                                    </div>
                                )}
                                <div className="mt10">
                                    <SmallFilterSelect
                                        name="objectTypeId"
                                        value={this.state.objectTypeId}
                                        handleChange={this.handleChange.bind(this)}
                                        topLabel={this.props.t('common.objectType')}
                                        placeholder={this.props.t('common.allItems', this.props.t('common.types'))}
                                        options={this.state.objectTypes}
                                    />
                                </div>
                                <div className="mt10">
                                    <SmallFilterSelect
                                        name="inspectionStandardType"
                                        value={this.state.inspectionStandardType}
                                        handleChange={this.handleChange.bind(this)}
                                        topLabel={
                                            this.props.useProductTitle ? this.props.t('product.productType') : this.props.t('common.inspectionType')
                                        }
                                        placeholder={this.props.t('common.allItems', this.props.t('common.types'))}
                                        options={getInspectionStandardTypeOptions(this.props.t)}
                                    />
                                </div>
                                {this.state.inspectionStandardType && (
                                    <>
                                        {customInspectionTypeFrontEndSettings[this.state.inspectionStandardType] ? (
                                            <>
                                                {customInspectionTypeFrontEndSettings[this.state.inspectionStandardType]!
                                                    .getSurveyConditionFilterOptions && (
                                                    <div className="mt10 pb10">
                                                        <EditPanelFilterMultiSelect
                                                            name="conditions"
                                                            value={this.state.conditions ?? []}
                                                            handleChange={this.handleChange.bind(this)}
                                                            topLabel={this.props.t('common.condition')}
                                                            placeholder={this.props.t('common.selectItem', this.props.t('common.result'))}
                                                            options={customInspectionTypeFrontEndSettings[this.state.inspectionStandardType]!
                                                                .getSurveyConditionFilterOptions!()}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {' '}
                                                <div className="mt10 pb10">
                                                    <EditPanelFilterMultiSelect
                                                        name="conditions"
                                                        value={this.state.conditions ?? []}
                                                        handleChange={this.handleChange.bind(this)}
                                                        topLabel={this.props.t('common.condition')}
                                                        placeholder={this.props.t('common.allItems', this.props.t('common.conditions'))}
                                                        options={[
                                                            { value: '1', text: this.props.t('condition.excellent') },
                                                            { value: '2', text: this.props.t('condition.good') },
                                                            { value: '3', text: this.props.t('condition.reasonable') },
                                                            { value: '4', text: this.props.t('condition.moderate') },
                                                            { value: '5', text: this.props.t('condition.poor') },
                                                            { value: '6', text: this.props.t('condition.bad') },
                                                            { value: 'F', text: this.props.t('condition.further') },
                                                            { value: 'U', text: this.props.t('condition.unknown') },
                                                        ]}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                {this.state.inspectionStandardType === InspectionStandardTypes.Fmeca && (
                                    <div className="mt10 pb10">
                                        <SmallFilterSelect
                                            name="riskAssessment"
                                            value={this.state.riskAssessment}
                                            handleChange={this.handleChange.bind(this)}
                                            topLabel={this.props.t('cra.riskAssessment')}
                                            placeholder={this.props.t('common.selectItem', this.props.t('cra.riskAssessment'))}
                                            options={[
                                                { value: '1', text: this.props.t('condition.orMore', this.props.t('cra.riskScore1')) },
                                                { value: '2', text: this.props.t('condition.orMore', this.props.t('cra.riskScore2')) },
                                                { value: '3', text: this.props.t('condition.orMore', this.props.t('cra.riskScore3')) },
                                                { value: '4', text: this.props.t('cra.riskScore4') },
                                            ]}
                                        />
                                    </div>
                                )}
                                {this.props.useBatches && (
                                    <div className="mt10">
                                        <SmallFilterSelect
                                            name="batchId"
                                            value={this.state.batchId}
                                            handleChange={this.handleChange.bind(this)}
                                            topLabel="Batch"
                                            placeholder={this.props.t('common.allItems', 'Batches')}
                                            options={this.state.batches}
                                        />
                                    </div>
                                )}
                                <div className="mt10">
                                    <EditPanelFilterMultiSelect
                                        name="selectedSurveyLabels"
                                        topLabel={this.props.t('survey.filterObjectsBySurveyLabel')}
                                        placeholder={this.props.t('common.allItems', this.props.t('common.labels'))}
                                        value={this.state.selectedSurveyLabels}
                                        options={this.state.knownLabels}
                                        handleChange={this.handleSurveyLabelChange.bind(this)}
                                    />
                                </div>
                                <div className="mt20 mb10">
                                    <InputSearchText
                                        name="codeDescription"
                                        value={this.state.codeDescription}
                                        handleChange={this.handleChange.bind(this)}
                                        handleEnter={async () => await this.search()}
                                        topLabel={this.props.t('common.description')}
                                    />
                                </div>
                            </div>
                        </form>
                        {(this.state.loading || this.state.searching) && !this.state.noDataAvailable && (
                            <div className="text-center" style={{ position: 'absolute', top: '30%', left: '40%', zIndex: 100 }}>
                                <Spinner />
                            </div>
                        )}

                        {this.state.noDataAvailable && (
                            <div className="text-center" style={{ position: 'absolute', top: '30%', left: '40%', zIndex: 100 }}>
                                <DisplayMessage message={this.props.t('survey.noItemsOnMap')} messageType="noData" />
                            </div>
                        )}

                        {this.state.items && (
                            <GenericMap
                                mapLoaded={this.mapLoaded.bind(this)}
                                zoom={11}
                                center={{ lat: this.state.latitude, lng: this.state.longitude }}
                                isPdokMapLayerAvailable={this.props.isPdokMapLayerAvailable}
                                isDroneNoFlyMapNlAvailable={this.props.isDroneNoFlyMapNlAvailable}
                                markers={this.state.items.map((object) => ({
                                    key: object.id,
                                    position: { lat: object.latitude, lng: object.longitude },
                                    title: object.name,
                                    onClick: this.handleMarkerRightclick.bind(this, object),
                                    color: codeToColorCode(object.lastSurveyCondition, object.lastSurveyInspectionStandardType),
                                    shapeGeoJson: object.shapeGeoJson ? JSON.parse(object.shapeGeoJson) : undefined,
                                    conditionLabel: getConditionLabelForMap(object.lastSurveyCondition, object.lastSurveyInspectionStandardType),
                                    condition: object.lastSurveyCondition,
                                    isTerrain:
                                        object.objectTypeName?.toLowerCase() === 'terrein' || object.objectTypeName?.toLowerCase() === 'terrain',
                                }))}
                                shouldClusterMarkers={this.props.shouldClusterMarkers}
                                onBoundsChanged={async (bounds) => {
                                    await this.setState({ mapBounds: bounds });
                                    await this.search.bind(this)(true);
                                }}
                                onZoomChanged={async () => {
                                    await this.search.bind(this)(true);
                                }}
                                markersToFit={this.state.markersToFit}
                            />
                        )}
                    </div>
                </section>
            </span>
        );
    }

    private async loadObjectTypes() {
        const result = await fetchGet('/api/objectTypesAvailable', {}, ['NO_DATA_AVAILABLE'], [404]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({ value: item.id, text: item.name }));
            await this.setState({ objectTypes: valueTextArray });
        }
    }

    private async loadObjectComplexLocations() {
        const result = await fetchGet('/api/objectsComplexList', {});
        await this.setState({ objectComplexLocations: result.json?.items });
    }

    private async loadBatches() {
        const queryParamters = {} as { [key: string]: string };
        if (this.state.inspectionStandardType) {
            queryParamters.inspectionStandardType = this.state.inspectionStandardType;
        }
        const result = await fetchGet('/api/batcheslist', queryParamters, [], [403]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({
                value: item.id,
                text: `${item.name.substring(0, 42)}${item.name.length > 43 ? '...' : ''}`,
            }));
            await this.setState({ batches: valueTextArray });
        } else {
            await this.setState({ batches: [] });
        }
    }

    private async mapLoaded() {
        await this.setState({ loading: false });
    }

    private handleMarkerRightclick(object: any) {
        this.props.navigate(`/surveys/${object.lastSurveyId}`);
    }

    private async handleChange(value: any, stateKey: string) {
        if (stateKey === 'batchId') {
            await this.setState({ batchId: value, objectTypeId: undefined });
        } else {
            const newState = {} as any;
            newState[stateKey] = value;
            if (stateKey === 'inspectionStandardType') {
                newState.conditions = [];
            }
            await this.setState(newState);
        }
    }

    private async search(isKeepMapPosition = false) {
        if (!this.state.mapBounds) {
            await this.setState({ items: [] });
            return;
        }
        if (this.state.isFirstSearch) {
            isKeepMapPosition = false;
            await this.setState({ isFirstSearch: false });
        }
        const url = '/api/surveymarkers';

        store2.set(storeKey, this.state.objectTypeId);
        await this.setState({ searching: true, noDataAvailable: false });
        const result = await fetchGet(url, {
            objectTypeId: this.state.objectTypeId ? this.state.objectTypeId : undefined,
            batchId: this.state.batchId ? this.state.batchId : undefined,
            inspectionStandardType: this.state.inspectionStandardType,
            conditions: this.state.conditions?.join(),
            risk: this.state.riskAssessment,
            locationCode: this.state.locationCode ? this.state.locationCode : undefined,
            codeDescription: this.state.codeDescription,
            surveyLabels: this.state.selectedSurveyLabels,
            boundingBoxXMin: this.state.mapBounds.xMin,
            boundingBoxXMax: this.state.mapBounds.xMax,
            boundingBoxYMin: this.state.mapBounds.yMin,
            boundingBoxYMax: this.state.mapBounds.yMax,
            limit: this.props.shouldClusterMarkers ? 5000 : 2000,
        });

        if (result.json.items.length === 0) {
            await this.setState({ noDataAvailable: true, items: [], markersToFit: [] });
        } else {
            const { hasItemsInBoundingBox } = result.json;
            if (hasItemsInBoundingBox) {
                await this.setState({ items: result.json.items, markersToFit: [] });
            } else {
                if (!isKeepMapPosition) {
                    const markersToFit = result.json.items.map((object: IObject) => ({
                        key: object.id,
                        position: { lat: object.latitude, lng: object.longitude },
                        shapeGeoJson: object.shapeGeoJson ? JSON.parse(object.shapeGeoJson) : undefined,
                    }));
                    await this.setState({ items: result.json.items, markersToFit });
                }
            }
        }
        await this.setState({ searching: false });
    }

    private async loadKnownLabels() {
        const url = '/api/surveys/knownLabels';
        const result = await fetchGet(url);
        const knownLabels = transformLabelsToSelectOptions(result?.json);
        await this.setState({ knownLabels });
    }

    private async handleSurveyLabelChange(value: string[]) {
        await this.handleChange(value, 'selectedSurveyLabels');
        await this.search();
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(SurveysMap));
